import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { DataFieldTypes } from '@app/enums/data-field-types.enum';
import { DataValue } from '@models/employee/data-value.model';
import { DataFieldGroup } from '@models/settings/data-field-group.model';
import { DataField } from '@models/settings/data-field.model';
import { uniqBy } from 'lodash-es';
import { DataFieldGroupValuesComponent } from '../data-field-group-values/data-field-group-values.component';

@Component({
    selector: 'app-data-field-groups',
    templateUrl: './data-field-groups.template.html',
    styleUrls: ['./data-fields-groups.style.scss'],
})
export class DataFieldGroupsComponent implements OnInit {
    private static nextId = 0;
    @Input() id = `data-field-group-${(DataFieldGroupsComponent.nextId += 1)}`;
    @Input() dataFields!: DataField[];
    @Input() dataValues!: DataValue[];
    @Input() canEditDataValues = false;
    @Input() component!: string;
    @Input() withMargin = true;

    @Output() edit: EventEmitter<string> = new EventEmitter<string>();

    @ViewChildren(DataFieldGroupValuesComponent)
    dataFieldGroupValuesComponents?: QueryList<DataFieldGroupValuesComponent>;

    dataFieldTypes = DataFieldTypes;
    customDataFieldGroups?: DataFieldGroup[];
    editableFieldsCountPerGroup: { [groupId: number]: number } = {};

    ngOnInit(): void {
        this.setCustomDataFieldGroups();
        this.setEditableFieldsCountPerGroup();
    }

    editFieldGroup(group: string): void {
        this.edit.emit(group);
    }

    setCustomDataFieldGroups(): void {
        if (!this.dataFields || !this.component) {
            return;
        }

        const groups = this.dataFields
            .filter((df: DataField) => df.dataFieldGroup?.component === this.component && df.dataFieldGroup.isCustom)
            .map((df: DataField) => df.dataFieldGroup);

        const doesGroupsHaveOrdering = groups.some((group) => group.orderBy !== null);
        const groupsOrderingColumn = doesGroupsHaveOrdering ? 'orderBy' : 'id';
        groups.sort((a, b) => (a[groupsOrderingColumn] ?? 0) - (b[groupsOrderingColumn] ?? 0));

        this.customDataFieldGroups = uniqBy(groups, 'id');
    }

    setEditableFieldsCountPerGroup(): void {
        this.editableFieldsCountPerGroup =
            this.customDataFieldGroups?.reduce((counts: { [gid: number]: number }, g: DataFieldGroup) => {
                counts[g.id] = this.countEditableFieldsForGroup(g.id);
                return counts;
            }, this.editableFieldsCountPerGroup) ?? {};
    }

    countEditableFieldsForGroup(groupId: number): number {
        const dataFieldGroup = this.customDataFieldGroups?.find((dfg: DataFieldGroup) => dfg.id === groupId);
        if (!dataFieldGroup) {
            return 0;
        }

        return this.dataFields.filter((df: DataField) => df.dataFieldGroupId === dataFieldGroup.id && df.editable)
            .length;
    }

    resetDataValues(): void {
        this.dataFieldGroupValuesComponents?.forEach((component) => component.resetDataValues());
    }

    get labelId(): string {
        return `${this.id}-label`;
    }
}
